import { useMemo } from "react";

/**
 * Determines device readiness for the mic/speaker check screen.
 *
 * A device is considered "real" (not a Chrome placeholder) only when it has
 * both a non-empty `deviceId` AND a non-empty `label`. Chrome returns
 * placeholder entries with `deviceId: ""` and `label: ""` when the user
 * has not yet granted (or has denied) permission.
 *
 * `devicesReady` gates on `micReady` and, when `isRecordSessions` is true,
 * also on `cameraReady` — because recording requires a working camera.
 *
 * @param {{ audioInputDevices: MediaDeviceInfo[], audioOutputDevices: MediaDeviceInfo[], videoInputDevices?: MediaDeviceInfo[], isRecordSessions?: boolean }} params
 * @returns {{ devicesReady: boolean, micReady: boolean, speakerReady: boolean, cameraReady: boolean }}
 */
export default function useDeviceReadiness({ audioInputDevices, audioOutputDevices, videoInputDevices = [], isRecordSessions = false }) {
  const micReady = useMemo(
    () => audioInputDevices.some((d) => d.deviceId && d.label && d.label.trim() !== ""),
    [audioInputDevices]
  );

  const speakerReady = useMemo(
    () => audioOutputDevices.some((d) => d.deviceId && d.label && d.label.trim() !== ""),
    [audioOutputDevices]
  );

  const cameraReady = useMemo(
    () => videoInputDevices.some((d) => d.deviceId && d.label && d.label.trim() !== ""),
    [videoInputDevices]
  );

  const devicesReady = isRecordSessions ? micReady && cameraReady : micReady;

  return useMemo(
    () => ({ devicesReady, micReady, speakerReady, cameraReady }),
    [devicesReady, micReady, speakerReady, cameraReady]
  );
}
